import { HStack, VStack, Text, Avatar, Box, Badge, Icon } from '@chakra-ui/react'
import { LuCircleCheck, LuClock, LuUser } from 'react-icons/lu'
import { useTranslation } from 'react-i18next'
import { useMyProfile } from '../../api/api_hooks/user/myProfile'

interface OperatorBadgeProps {
  /** Override — display a specific operator name (e.g. from saved case) */
  operatorName?: string | null
  /** Override — display a specific timestamp (ISO string) */
  validatedAt?: string | null
  /** If true, show the current logged-in user as operator (live session) */
  useCurrentUser?: boolean
  /** Compact single-line mode */
  compact?: boolean
}

/**
 * Displays who validated the exam and when.
 * Always shown when operator tracking is enabled in CHU config.
 */
export const OperatorBadge: React.FC<OperatorBadgeProps> = ({
  operatorName,
  validatedAt,
  useCurrentUser = false,
  compact = false,
}) => {
  const { t } = useTranslation()
  const { data: profile } = useMyProfile()

  const name = operatorName
    ?? (useCurrentUser ? profile?.contactName ?? profile?.email ?? '—' : null)

  const timestamp = validatedAt
    ? new Date(validatedAt).toLocaleString('fr-FR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      })
    : null

  if (!name && !timestamp) return null

  if (compact) {
    return (
      <HStack spacing={1} fontSize="xs" color="gray.500">
        <Icon as={LuCircleCheck} color="green.500" boxSize={3} />
        <Text>{t('chu.operator.validatedBy')} <strong>{name ?? '—'}</strong></Text>
        {timestamp && (
          <>
            <Text>·</Text>
            <Text>{timestamp}</Text>
          </>
        )}
      </HStack>
    )
  }

  return (
    <Box
      bg="green.50"
      border="1px solid"
      borderColor="green.200"
      borderRadius="lg"
      px={4}
      py={3}
    >
      <HStack spacing={3} align="start">
        <Icon as={LuCircleCheck} color="green.500" boxSize={5} mt={0.5} />
        <VStack align="start" spacing={0.5}>
          {name && (
            <HStack spacing={1}>
              <Icon as={LuUser} boxSize={3} color="gray.500" />
              <Text fontSize="sm" color="gray.700">
                {t('chu.operator.validatedBy')}{' '}
                <Text as="span" fontWeight="semibold">{name}</Text>
              </Text>
            </HStack>
          )}
          {timestamp && (
            <HStack spacing={1}>
              <Icon as={LuClock} boxSize={3} color="gray.500" />
              <Text fontSize="xs" color="gray.500">
                {t('chu.operator.validatedAt')} {timestamp}
              </Text>
            </HStack>
          )}
        </VStack>
      </HStack>
    </Box>
  )
}

/** Pending badge — shown when validation is not yet done */
export const OperatorPendingBadge: React.FC = () => {
  const { t } = useTranslation()
  return (
    <Badge colorScheme="orange" variant="subtle" fontSize="xs" px={2} py={0.5} borderRadius="full">
      {t('chu.operator.noValidation')}
    </Badge>
  )
}
